import * as PIXI from 'pixi.js';
import type { TimeRange } from '../';
import {
    assertBoolean,
    assertNumberBetween,
    assertObject,
    assertString,
    assertUuid,
} from '../validators';
import type { DestroyOptions, FetchResources, LifecycleAware, SceneCategory } from '../types';
import { AspectRatio } from '../aspectRatioConfig';
import { addAssetToContainer } from '../libs/buildChildrenForScene';
import { SequentialTimeline } from '../libs/SequentialTimeline';
import { Config } from '../video-data/config';
import type { TextDirection, SeekMode } from '../enums';
import { BufferingMode } from '../enums';
import { BufferingAbortedError } from '../errors';
import { hasOwnProperty } from '../common';
import uuid from '../video-data/utils/uuid';
import { AudioAsset } from '../audio/AudioAsset';
import { ErrorReporter } from '../libs/error-reporter';
import { Scene } from './scene/Scene';
import type { AddSceneSettings } from './scene/types';
import type { SerializableMixinSettings } from './serializableMixin';
import type { SceneGroupManager } from './SceneGroupManager';
import { BaseGroup } from './BaseGroup';
import { TimeAwareBase } from './TimeAwareBase';
import type { Asset, AssetType, GetAssetType, GetAssetsOptions } from './asset/BaseAsset';
import { CaptionsManager } from './captions/CaptionsManager';
import type { UpdateFlowMixinSettings, UpdateFlowMixinInterface } from './updateFlowMixin';
import { TimeAwareMediaAsset } from './asset/TimeAwareMediaAsset';
import { Transcript } from './Transcript';
import { syncTranscriptData } from './asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import { CompositingAsset } from './CompositingAsset';

export type SceneGroupSettings = {
    sceneGroupManager: SceneGroupManager;
    fetchResources: FetchResources;
    preserveAssetIds?: boolean;
    enableHelpers?: boolean;
    autoDraw?: boolean;
    disableCaptions?: boolean;
} & SerializableMixinSettings &
    UpdateFlowMixinSettings;

/**
 * SceneGroups contain a number of re-orderable scenes. The scenes in a SceneGroup must be a
 * continuous group of scenes in Luminary. Scenes cannot be moved to another scene group. They are
 * identified by their id (sceneGroup.id), which can be used to move or remove the scene group
 * through the SceneGroupManager. Manipulating scenes using the methods from this class will also
 * be reflected in Luminary (moving, removing, adding scenes will update the group and Luminary scenes).
 */
export class SceneGroup extends TimeAwareBase {
    override _classType = Config.SCENE_GROUP_TYPE;
    override _name = Config.SCENE_GROUP_TYPE;
    readonly captions: CaptionsManager;
    readonly avatar: CompositingAsset;
    #aspectRatio: AspectRatio = AspectRatio.LANDSCAPE;
    #scenes: Scene[] = [];
    #scenesById = new Map<string, Scene>();
    #preserveAssetIds: boolean;
    #enableHelpers: boolean;
    readonly #sceneGroupManager: SceneGroupManager;
    readonly #workspace: BaseGroup;
    readonly #scenesWorkspace: BaseGroup;
    readonly #assetsWorkspace: BaseGroup;
    readonly #helperScene = new PIXI.Container();
    #musicVolumeOverride: number = null;
    #autoDraw = false;
    #timeline = new SequentialTimeline({
        name: 'scene_group_timeline',
        duration: Config.DEFAULT_SCENE_DURATION,
    });

    #abortBuffering: (error: BufferingAbortedError) => void = () => {};
    // Returns true if any asset has changed since the last time we called animate
    #assetsChangedSinceLastAnimate: boolean = true;
    #lastAnimatedTime: number = -1;

    #mediaAsset: TimeAwareMediaAsset = null;
    #voiceoverAsset: AudioAsset = null;
    #voiceoverAssetMp3Url: string | null = null;
    #originalTranscript: Transcript;

    constructor(settings: SceneGroupSettings) {
        super(settings);
        this.#sceneGroupManager = settings.sceneGroupManager;
        this.#preserveAssetIds = settings.preserveAssetIds;
        this.#enableHelpers = settings.enableHelpers;

        if (settings.hasOwnProperty('autoDraw')) {
            this.#autoDraw = settings.autoDraw;
        }

        // Here we compose the following PIXI hierarchy
        // + workspace (gets rendered)
        // +-+ scenesWorkspace (contains our scenes)
        // +-+ assetsWorkspace (contains any scene group level assets)
        //   +-+ captionManager
        //   +-+ helperScene

        this.#workspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            isFinalTopLevel: true,
            name: 'sceneGroupWorkspace',
            updateEventSource: this, // any child changes eventually get emitted through `this.on('childUpdated')`
        });
        this.#workspace.visible = false;

        // scenes DO NOT bubble up their changes and are instead manually propagated
        // we do this so we can better control what gets updated -- see slide for more details
        this.#scenesWorkspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            isFinalTopLevel: false,
            name: 'scenesWorkspace',
        });

        // all updates pass through this.#workspace
        this.#assetsWorkspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            isFinalTopLevel: false,
            updateParentOnRedraw: true,
            name: 'assetsWorkspace',
        });

        this.#workspace.insertAsset(this.#scenesWorkspace);
        this.#workspace.insertAsset(this.#assetsWorkspace);

        let disableCaptions = false;
        if (settings.hasOwnProperty('disableCaptions')) {
            disableCaptions = settings.disableCaptions;
        }

        // all updates pass through this.#assetsWorkspace which bubble up to this
        this.captions = new CaptionsManager(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            name: 'captionManager',
            isFinalTopLevel: true,
            updateParentOnRedraw: true,
            enableBoundsWithoutAnimationForSubtree: true,
            disableCaptions,
        });
        this.#assetsWorkspace.insertAsset(this.captions);
        this.#assetsWorkspace._addPixiChild(this.#helperScene);

        this.avatar = new CompositingAsset(this, {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            autoDraw: this.autoDraw,
            name: 'AvatarAsset',
            updateLuminaryParentOnRedraw: true,
        });

        this.#originalTranscript = new Transcript({
            parent: this,
            fetchResources: this.fetchResources,
            autoUpdate: this.autoUpdate,
        });

        // events
        this.#setupOnStart();
        this.#setupOnEnd();

        // this event gets emitted by any child change from:
        // - this.#workspace
        // - any slide which manually calls _emitChildUpdate() or has any of it's children update
        this.on('childUpdated', (name: string, sceneIndex: number, subsceneIndex: number) => {
            this.#assetsChangedSinceLastAnimate = true;

            // Propagate asset update flow changes to our base update flow manually.
            // The audio manager is dependent on scene properties (media asset tracks), such
            // as volume and duration. When we update these properties we don't
            // trigger the root Luminary update loop. This is mostly fine except for audio.
            // Changes to the media asset (e.g. volume) needs to propagate to the manager.
            if (!this.root.requiresRedraw) {
                this.root.atomicUpdate(() => {
                    this.root._updateAudioAnimationManager();
                });
            }

            // bubble up changed event to root
            this.root._emitChanged();

            // render all preview images for each scene for a scene group asset level change
            if (name === this.#workspace.name) {
                this.scenes.forEach((scene) => {
                    scene._emitViewChanged();
                });

                if (this.#enableHelpers) {
                    this.#updateHelpers();
                }
            }
            // render the specified scene images for a scene level change
            else if (name === 'scene') {
                this.scenes[sceneIndex]?._emitViewChanged(subsceneIndex);
            }
            // render nothing if nothing is specified
        });
    }

    /**
     * Play the scene group. If no time range is given, then plays from the start to the end of the group.
     * Time is relative to the scene group. { startTime: 0, endTime: sceneGroup.duration } plays the entire scene group.
     * If one wants to play after pausing, pass { startTime: sceneGroup.currentTime }.
     */
    async play(playRange?: Partial<TimeRange>) {
        const adjustPlayRangeToGlobal = (playRange?: Partial<TimeRange>) => {
            const startTime =
                typeof playRange?.startTime === 'number'
                    ? playRange.startTime + this.startTime
                    : this.startTime;
            const endTime =
                typeof playRange?.endTime === 'number'
                    ? playRange.endTime + this.startTime
                    : this.endTime;

            return { startTime, endTime };
        };

        await this.root._play(adjustPlayRangeToGlobal(playRange), this);
    }

    /**
     * Get all assets in our hierarchy
     */
    getAssets(options: GetAssetsOptions = { deep: true }): Asset[] {
        // we want to filter out the canvas caption asset here as it is part of the scene group and serialized as such
        const children = this.#workspace
            .getAssets(options)
            .filter((asset) => Config.isAsset && !Config.isCaptionAsset(asset));
        if (this.mediaAsset) {
            children.push(this.mediaAsset);
        }
        children.push(...this.avatar.getAssets(options));
        return children;
    }

    /**
     * Get all assets of the given classType in our hierarchy.
     * @param classType - the classType of the component we're searching for
     */
    getAssetsByType<T extends AssetType>(
        classType: T,
        options?: GetAssetsOptions,
    ): GetAssetType<T>[];
    getAssetsByType(classType: AssetType, options?: GetAssetsOptions) {
        const children = this.#workspace.getAssetsByType(classType, options);
        if (classType === Config.MEDIA_ASSET_TYPE && this.mediaAsset) {
            children.push(this.mediaAsset);
        }
        const assetsByType = this.avatar.getAssetsByType(classType, options);
        if (assetsByType) {
            children.push(...assetsByType);
        }
        return children;
    }

    getAssetById(id: string, options?: GetAssetsOptions): Asset | undefined {
        if (this.mediaAsset?.id === id) {
            return this.mediaAsset;
        }
        if (this.avatar?.foreground?.id === id) {
            return this.avatar.foreground;
        }
        if (this.avatar?.background?.id === id) {
            return this.avatar.background;
        }
        return this.#workspace.getAssetById(id, options);
    }

    generateNewAssetIds() {
        if (this.mediaAsset) {
            this.mediaAsset.generateNewId();
        }
        this.avatar.generateNewAssetIds();
        this.scenes.forEach((scene) => scene.generateNewAssetIds());
    }

    /**
     * @internal
     */
    _updateAspectRatio(value: AspectRatio) {
        if (this.#aspectRatio === value) {
            return;
        }
        this.#aspectRatio = value;
        this.captions._aspectRatio = value;
        this.scenes.forEach((scene) => scene._updateAspectRatio(value));
    }

    get requiresRedraw() {
        const allAssets = [this.captions, this.avatar, ...this.scenes];
        return allAssets.some((asset) => asset.requiresRedraw);
    }

    #updateCaptionsDuration = () => {
        // The duration of the captions manager is dependent on the duration of the voice driven asset.
        // Each time the asset changes we need to update the captions manager duration.
        if (this.mediaAsset) {
            if (!this.mediaAsset.isSourceDataSet) {
                return;
            }
            this.captions.asset.playbackRegions = this.mediaAsset.playbackRegions;
        } else if (this.voiceoverAsset) {
            this.captions.asset.playbackRegions = this.voiceoverAsset.playbackRegions;
        }
    };

    #updateAvatarDuration = () => {
        // we don't want to sync up the background asset since it's not generated from the voiceover asset,
        // only the foreground or the actual avatar actor is
        if (this.voiceoverAsset && this.avatar.foreground) {
            // wait for voiceoverAsset to load and is set first
            if (!this.voiceoverAsset.isLoaded) {
                return;
            }
            this.avatar.startTime = this.voiceoverAsset.startTime;
            this.avatar.endTime = this.voiceoverAsset.endTime;
        }
    };

    _updateDurationBasedOnMediaAsset = () => {
        // We should make sure that duration of scene group is always less than or equal to the duration of the media asset.
        // When we apply media asset to a scene group, we don't know the duration of the media asset
        // until it loads.
        // different browsers calculate the duration of the media asset differently.
        // This is why we need to adjust the duration in particular runtime like in chrome, safari or nodejs.
        // The duration of scene group with media asset is dependent on the duration of the media asset.
        // Each time the asset changes we need to update the scene group duration.
        // We can do that by adjusting the duration of the last subscene.

        if (!this.mediaAsset) {
            return;
        }

        // wait for the media asset to load
        if (this.mediaAsset.duration === 0) {
            return;
        }

        if (this.mediaAsset.duration >= this.duration) {
            return;
        }

        let delta = this.duration - this.mediaAsset.duration;
        if (delta > 500) {
            const errorMessage = `The duration of the scene group is greater than the duration of the media asset. It usually means that you need to wrap sequence of operations in the 'batchRun' call`;
            console.error(errorMessage);
            ErrorReporter.capture(errorMessage, {
                level: 'error',
                extra: {
                    delta,
                    sceneGroupDuration: this.duration,
                },
            });
            return;
        }

        while (delta > 0) {
            const lastScene = this.lastScene();
            if (!lastScene) {
                return;
            }
            const lastSubscene = lastScene.subscenes[lastScene.subscenes.length - 1];
            if (!lastSubscene) {
                return;
            }
            if (lastSubscene.duration <= delta) {
                // the delta is longer than the last subscene duration.
                // remove the whole subscene!
                delta -= lastSubscene.duration;
                lastScene.removeSubslide(lastSubscene.index);
                if (lastScene.subscenes.length === 0) {
                    this.removeScene(lastScene.id);
                }
            } else {
                // the subscene duration just needs to be changed by delta
                lastSubscene.duration -= delta;
                delta = 0;
            }
        }
    };

    #createHelpers() {
        this.#assetsWorkspace._getAllBaseAssetsWithDisplayedHelpers().forEach((child) => {
            child.createHelpers().forEach((helper) => {
                this.#helperScene.addChild(helper);
            });
        });
    }

    #removeHelpers() {
        while (this.#helperScene.children.length !== 0) {
            this.#helperScene.children[0].destroy(true);
        }
    }

    #updateHelpers() {
        this.#removeHelpers();
        this.#createHelpers();
    }

    #animateHelpers() {
        this.#helperScene.children.forEach((helper) => {
            (helper as unknown as UpdateFlowMixinInterface).requiresUpdate = true;
        });
    }

    /**
     * @internal
     */
    get _helperScene() {
        return this.#helperScene;
    }

    /**
     * @internal
     */
    override _update() {
        this.#linkMediaAssetsToSceneGroup();
        this.#updateCaptionsDuration();
        this.#updateAvatarDuration();
        this.#updateFancyCaptionTranscript();
        this.#updateMediaAssetIfSceneGroupContainsWaveform();

        if (this.#enableHelpers) {
            this.#updateHelpers();
        }
    }

    #updateMediaAssetIfSceneGroupContainsWaveform() {
        const waveformAssets = this.getAssetsByType(Config.LOTTIE_ASSET_TYPE)
            .concat(this.getAssetsByType(Config.ICON_ASSET_TYPE))
            .filter((asset) => asset.playbackMode === Config.PLAYBACK_WAVEFORM);
        if (waveformAssets.length === 0) {
            return;
        }

        const audioAssetForWaveform = (() => {
            if (this.voiceoverAsset) {
                return this.voiceoverAsset;
            }

            if (this.mediaAsset) {
                if (this.mediaAsset._audioAsset && this.mediaAsset.sourceData.audio_url) {
                    this.mediaAsset._audioAsset.url = this.mediaAsset.sourceData.audio_url;
                    return this.mediaAsset._audioAsset;
                }

                this.mediaAsset._audioAsset.url = undefined;
            }
            return undefined;
        })();

        // Note that when there are no waveform assets, it shouldn't matter what the mediaAsset._audioAsset.url is.
        waveformAssets.forEach((waveform) => {
            waveform._waveformAudioAsset = audioAssetForWaveform;
        });
    }

    #updateFancyCaptionTranscript() {
        this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE).forEach((asset) =>
            syncTranscriptData(asset, this.originalTranscript),
        );
    }

    /**
     * Returns the playback regions of the sceneGroup's media or voiceover asset.
     */
    get voicedrivenAssetPlaybackRegions() {
        if (this.mediaAsset) {
            return this.mediaAsset.playbackRegions;
        }
        if (this.voiceoverAsset) {
            return this.voiceoverAsset.playbackRegions;
        }
        return [];
    }

    get voicedrivenAssetDuration() {
        if (this.mediaAsset) {
            return this.mediaAsset.sourceDuration;
        }
        if (this.voiceoverAsset) {
            return this.voiceoverAsset.sourceDuration;
        }
        return 0;
    }

    /**
     * Returns true if the scene group has any audio.
     */
    get hasAudio() {
        if (this.mediaAsset?.hasAudio) {
            return true;
        }
        if (this.voiceoverAsset) {
            return true;
        }
        return this.scenes.some((scene) => scene.hasAudio);
    }

    /**
     * Add a scene to the group (The settings are the same as Luminary.addScene)
     */
    addScene(settings: AddSceneSettings): Scene {
        const luminary = this.root;
        let id = settings?.id;

        // if not passed in an id, generate a new one
        if (id === undefined) {
            // first check if `data.id` is set
            if (hasOwnProperty(settings, 'data') && settings['data'].id) {
                id = settings.data.id as string;
            } else {
                id = uuid();
            }
        }
        if (this.#scenesById.get(id) !== undefined) {
            throw new Error('A scene id was specified, but a scene with this id already exists.');
        }

        assertUuid(id);

        if (!hasOwnProperty(settings, 'data')) {
            assertString(settings.sceneDesignId);
        } else {
            assertObject(settings.data);
        }

        const initAutoDraw = this.autoDraw;
        const initAutoUpdate = this.autoUpdate;
        this.autoDraw = false;
        this.autoUpdate = false;
        let scene: Scene;

        try {
            // create scene
            scene = new Scene({
                // since we're currently building a scene design on init, we have to ensure we don't try to process any
                // updates here since the scene isn't fully integrated into the system.
                id: id,
                sceneGroup: this,
                enableHelpers: this.#enableHelpers,
                autoUpdate: false,
                autoDraw: false,
                parent: this,
                fetchResources: this.fetchResources,
            });
            scene._template.load(luminary.template.save());

            scene._aspectRatio = luminary.aspectRatio;
            scene.textDirection = luminary.textDirection;

            // after applying the global properties to the scene, load the data of scene design data
            if (hasOwnProperty(settings, 'data')) {
                scene.load(settings.data);
            } else {
                const sceneDesignData = luminary._getSceneDesignConfig(
                    settings.sceneDesignId,
                    luminary.aspectRatio,
                );
                scene.load(sceneDesignData);
                if (!this.#preserveAssetIds) {
                    scene.generateNewAssetIds();
                }
            }

            this.#verifySceneCategoryCount(scene, Config.CATEGORY_OUTRO, 1);
            this.#verifySceneCategoryCount(scene, Config.CATEGORY_BRAND, 1);

            this.#scenes.push(scene);
            this.#scenesById.set(id, scene);
            this.#scenesWorkspace.insertAsset(scene.sceneWorkspace);
            // TODO - we have similar logic in index._updateTransition()
            // TODO - clean up with LU-2447
            //  for now have to move transition applied on first scene to the scene group
            if (this.#scenes.length === 1 && scene._timeline.offset !== 0) {
                this.#timeline.offset = scene._timeline.offset;
                scene._timeline.offset = 0;
            }
            this.#timeline.add(scene._timeline);
            // communicate state change to our consumers. Since we're not drawing any changes, we won't get any stateChanged
            // events from our children. Hence, we have to call it manually here.
            this.requiresUpdate = true;
        } finally {
            // process any changes, if necessary, now that our scene is fully integrated
            this.autoUpdate = initAutoUpdate;
            this.autoDraw = initAutoDraw;
        }

        return scene;
    }

    #verifySceneCategoryCount(scene: Scene, category: SceneCategory, allowedCount = 1) {
        if (scene.sceneCategory !== category) {
            return;
        }
        const sceneCount = this.#scenes.length;
        if (sceneCount >= allowedCount) {
            scene.destroy(true);
            throw new Error(`Cannot add ${category} scene to existing scene group.`);
        }

        const currentSceneCount = this.#scenes.filter(
            (scene) => scene.sceneCategory === category,
        ).length;
        if (currentSceneCount >= allowedCount) {
            scene.destroy(true);
            throw new Error(`Cannot add more than one ${category} scene.`);
        }
    }

    /**
     * Add a brand new asset of type assetType to the scene
     */
    addAsset<T extends AssetType>(assetType: T, settings = {}): GetAssetType<T> {
        return addAssetToContainer(this.#assetsWorkspace, assetType, settings);
    }

    // TODO: rename it to talkingHeadMediaAsset: https://lumen5.atlassian.net/browse/LU-2720
    get mediaAsset() {
        return this.#mediaAsset;
    }

    /**
     * Add a MediaAsset to this SceneGroup
     *
     * Note that you still need to set sourceData and volume
     * after calling adding the media asset.
     */
    addMediaAsset(id?: string) {
        if (this.#mediaAsset) {
            throw new Error('Scene group already has a media asset. Cannot add media');
        }
        if (this.avatar.foreground || this.avatar.background) {
            throw new Error('Scene group already has an avatar asset. Cannot add media');
        }
        if (this.#voiceoverAsset) {
            throw new Error('Scene group already has a voiceover asset. Cannot add media asset.');
        }

        const settings: {
            isFinalTopLevel: boolean;
            fetchResources: typeof this.fetchResources;
            autoDraw: boolean;
            name: string;
            updateLuminaryParentOnRedraw: boolean;
            id?: string;
        } = {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            autoDraw: this.autoDraw,
            name: 'SceneGroupMedia',
            updateLuminaryParentOnRedraw: true,
        };
        if (id) {
            settings.id = id;
        }
        this.#mediaAsset = new TimeAwareMediaAsset(this, settings);

        // Register with Luminary asset index for O(1) lookups
        this.root._registerAsset(this.#mediaAsset);

        this.requiresUpdate = true;

        return this.#mediaAsset;
    }

    /**
     * Remove this group's MediaAsset
     */
    removeMediaAsset() {
        if (!this.#mediaAsset) {
            throw new Error('removeMediaAsset called while SceneGroup has no media');
        }
        const linkedAssets = this.#getLinkedMediaAssets();
        linkedAssets.forEach((media) => {
            media.clear();
        });
        this.#mediaAsset.destroy();
        this.#mediaAsset = null;
    }

    /**
     * @deprecated Use {@link avatar} instead. This is a temporary alias for backwards compatibility.
     */
    get avatarAsset(): TimeAwareMediaAsset | null {
        return this.avatar?.foreground ?? null;
    }

    /**
     * @deprecated Use avatar instead. This is a temporary alias for backwards compatibility.
     */
    addAvatarAsset(id?: string) {
        if (this.mediaAsset) {
            throw new Error('Scene group already has a media asset. Cannot add avatar asset.');
        }
        if (this.avatar?.foreground) {
            return this.avatar.foreground;
        }
        return this.avatar.addForeground(id);
    }

    get voiceoverAsset() {
        return this.#voiceoverAsset;
    }

    addVoiceoverAsset(id?: string) {
        if (this.#voiceoverAsset) {
            throw new Error('addVoiceoverAsset called while SceneGroup already has a voiceover');
        }
        if (this.#mediaAsset) {
            throw new Error('Scene group already has a media asset. Cannot add voiceover asset.');
        }
        const settings: {
            fetchResources: typeof this.fetchResources;
            name: string;
            autoUpdate: boolean;
            parent: SceneGroup;
            id?: string;
        } = {
            fetchResources: this.fetchResources,
            name: 'SceneGroupVoiceover',
            autoUpdate: this.autoUpdate,
            parent: this,
        };
        if (id) {
            settings.id = id;
        }
        this.#voiceoverAsset = new AudioAsset(settings);

        // Register with Luminary asset index for O(1) lookups
        // AudioAsset is not part of the Asset union type but still needs registration
        this.root._registerAsset(this.#voiceoverAsset as unknown as Asset);

        this.requiresUpdate = true;

        return this.#voiceoverAsset;
    }

    removeVoiceoverAsset() {
        if (!this.#voiceoverAsset) {
            throw new Error('removeVoiceoverAsset called while SceneGroup has no voiceover');
        }

        this.#voiceoverAsset.destroy();
        this.#voiceoverAsset = null;
    }

    /**
     * The original transcript of the scene group. This is the transcript used by fancy captions.
     * For regular captions, see the transcript on the captions api instead.
     */
    get originalTranscript(): Transcript {
        return this.#originalTranscript;
    }

    #getLinkedMediaAssets() {
        const mediaAssets = this.scenes.flatMap((s) => s.getAssetsByType(Config.MEDIA_ASSET_TYPE));
        return mediaAssets.filter((m) => m.linkToSceneGroup);
    }

    #getLinkedMediaAssetSource() {
        if (this.mediaAsset) {
            return this.mediaAsset;
        }
        if (this.avatar.foreground || this.avatar.background) {
            return this.avatar;
        }
    }

    #linkMediaAssetsToSceneGroup() {
        const parentMediaAsset = this.#getLinkedMediaAssetSource();
        if (!parentMediaAsset) {
            return;
        }
        const linkedAssets = this.#getLinkedMediaAssets();
        linkedAssets.forEach((media) => media.linkSourceTo(parentMediaAsset));
    }

    firstScene() {
        return this.#scenes[0];
    }

    lastScene() {
        return this.#scenes[this.size - 1];
    }

    /**
     * @internal
     */
    _nextScene(sceneId: string): Scene | null {
        const size = this.size;
        const curIndex = this.#getSceneIndex(sceneId);
        const nextIndex = curIndex + 1;
        if (nextIndex < size) {
            return this.#scenes[nextIndex];
        }
        const nextSceneGroup = this.#sceneGroupManager._nextSceneGroup(this.id);
        if (!nextSceneGroup) {
            return null;
        }
        return nextSceneGroup.firstScene();
    }

    /**
     * @internal
     */
    _prevScene(sceneId: string): Scene | null {
        const curIndex = this.#getSceneIndex(sceneId);
        const prevIndex = curIndex - 1;
        if (prevIndex >= 0) {
            return this.#scenes[prevIndex];
        }
        const prevSceneGroup = this.#sceneGroupManager._prevSceneGroup(this.id);
        if (!prevSceneGroup) {
            return null;
        }
        return prevSceneGroup.lastScene();
    }

    get isOutroSceneGroup() {
        return this.scenes.length === 1 && this.scenes[0].sceneCategory === Config.CATEGORY_OUTRO;
    }

    get isBrandingSceneGroup() {
        return this.scenes.length === 1 && this.scenes[0].sceneCategory === Config.CATEGORY_BRAND;
    }

    /**
     * Find the index of this SceneGroup in luminary.sceneGroups.
     */
    get index(): number {
        return this.#sceneGroupManager._getSceneGroupIndex(this.id);
    }

    /**
     * The sceneGroup's global musicVolumeOverride override (if true it silences the background track for
     * the duration of the sceneGroup)
     * If null, it means the override is not set and the sceneGroup will use the global music volume.
     */
    get musicVolumeOverride(): number | null {
        return this.#musicVolumeOverride;
    }

    set musicVolumeOverride(value) {
        if (value === this.#musicVolumeOverride) {
            return;
        }
        assertNumberBetween(value, 0, 100, true);
        this.#musicVolumeOverride = value;
        this.requiresUpdate = true;
    }

    override set autoUpdate(value: boolean) {
        if (this.autoUpdate === value) {
            return;
        }

        assertBoolean(value);
        this.#originalTranscript.autoUpdate = value;
        this.scenes.forEach((scene) => {
            scene.autoUpdate = value;
        });
        if (this.#voiceoverAsset) {
            this.#voiceoverAsset.autoUpdate = value;
        }
        super.autoUpdate = value;
    }

    override get autoUpdate(): boolean {
        return super.autoUpdate;
    }

    /**
     * Deeply sets autoDraw on all BaseAssets within this SceneGroup.
     */
    set autoDraw(value: boolean) {
        if (this.#autoDraw === value) {
            return;
        }
        assertBoolean(value);
        this.#autoDraw = value;

        // Setting autoDraw causes assets to build, then load.
        // We need to load the SceneGroup MediaAsset first so that scenes which depend on
        // it are not stuck waiting for the SceneGroup MediaAsset, creating a deadlock in the AssetNetworkManager.
        if (this.#mediaAsset) {
            this.#mediaAsset.autoDraw = value;
        }
        this.avatar.autoDraw = value;
        this.scenes.forEach((scene) => {
            scene.autoDraw = value;
        });
        this.#workspace.autoDraw = value;
    }

    get autoDraw(): boolean {
        return this.#autoDraw;
    }

    get currentTime() {
        return this.#timeline.currentTime;
    }

    /**
     * @internal
     */
    get _timeline() {
        return this.#timeline;
    }

    /**
     * @internal
     */
    async _propagateBuffering(bufferingMode: BufferingMode): Promise<void> {
        try {
            await this.#propagateBuffering(bufferingMode);
        } catch (e) {
            if (e instanceof BufferingAbortedError) {
                // Don't propagate an error, but rather just resolve
                return;
            }
            throw e;
        }
    }

    get isPlaying() {
        return this._timeline.playing() && this._timeline.active;
    }

    #setupOnStart() {
        this._timeline.on('start', () => this._propagatePlay());
    }

    #setupOnEnd() {
        this._timeline.on('end', () => this._propagatePause());
    }

    /**
     * @internal
     */
    _propagatePlay() {
        if (this.#mediaAsset) {
            this.#mediaAsset._propagatePlay();
        }
        if (this.#voiceoverAsset) {
            this.#voiceoverAsset._propagatePlay();
        }
        this.avatar._propagatePlay();
        this.#assetsWorkspace.getAssets().forEach((child) => {
            child._propagatePlay();
        });
        this.getActiveScenesAt(this.currentTime).forEach((scene) => {
            scene._propagatePlay();
        });
    }

    /**
     * @internal
     */
    _propagateTextDirection(textDirection: TextDirection) {
        this.captions._propagateTextDirection(textDirection);

        this.scenes.forEach((scene) => {
            scene.textDirection = textDirection;
        });
    }

    protected override _getLifecycleAwareObjects(): LifecycleAware[] {
        const objects: LifecycleAware[] = [this.#assetsWorkspace, ...this.scenes, this.avatar];
        if (this.#mediaAsset) {
            objects.push(this.#mediaAsset);
        }
        if (this.#voiceoverAsset) {
            objects.push(this.#voiceoverAsset);
        }
        return objects.filter((obj) => obj && !obj.destroyed);
    }

    /**
     * @internal
     */
    _emitAssetsCanPlay() {
        if (this.root) {
            this.root.emit('assetsCanPlay');
        }
    }

    seek(seekMode: SeekMode) {
        const timeChangedSinceLastAnimate = this.currentTime !== this.#lastAnimatedTime;
        const shouldAnimate = timeChangedSinceLastAnimate || this.#assetsChangedSinceLastAnimate;

        if (shouldAnimate) {
            if (this.#mediaAsset) {
                this.#mediaAsset.seek(this._timeline.progress, seekMode);
            }
            this.avatar.seek(this._timeline.progress, seekMode);
            this.#assetsWorkspace.seek(this._timeline.progress, seekMode);
            this.#animateHelpers();

            // animate the scenes separately because their assets are relative to the scene's timeline
            const activeScenes = this.getActiveScenesAt(this.currentTime);

            // we only want to show generic captions when:
            // - captions.visible is true
            // - all the scenes have displayCaptions set to true
            this.captions._setCaptionsAndDecorationsVisibility(
                this.captions.visible &&
                    activeScenes.every((scene) => scene.displayCaptions === true),
            );

            activeScenes.forEach((scene) => {
                scene.seek({ children: shouldAnimate, transition: true }, seekMode);
            });
        }

        this.#lastAnimatedTime = this.currentTime;
        this.#assetsChangedSinceLastAnimate = false;
    }

    async #propagateBuffering(bufferingMode: BufferingMode): Promise<void> {
        const activeScenes = this.getActiveScenesAt(this.currentTime);

        if (bufferingMode === BufferingMode.PrepareForPlayback) {
            // Seek all subsequent slides to 0. This is required to make sure following scenes'
            // time aware assets (like videos) will start at the right time.
            const startIndex = activeScenes.length
                ? this.#getSceneIndex(activeScenes[activeScenes.length - 1].id) + 1
                : 0;
            for (let i = startIndex; i < this.scenes.length; i++) {
                // Do not wait for buffering to resolve.
                // Scenes can continue buffering while we start playing.
                const sceneId = this.#scenes[i].id;
                const scene = this.#sceneGroupManager._getSceneById(sceneId);
                scene._propagateBuffering(BufferingMode.PrepareForPlayback).catch((e) => {
                    if (!scene.destroyed) {
                        if (!(e instanceof Error)) {
                            ErrorReporter.capture('received non-error', {
                                level: 'error',
                                extra: {
                                    originalErrorObject: e,
                                },
                            });
                        } else {
                            ErrorReporter.capture(e, {
                                extra: {
                                    action: 'seek future scenes',
                                },
                            });
                        }
                    }
                });
            }

            // If this scene group is in the future then prepare the assets on the scene group
            if (this.startTime > this.root.currentTime) {
                if (this.#mediaAsset) {
                    // This is preparing for the future so don't wait for media asset to buffer
                    void this.#mediaAsset._bufferAtTime(0, bufferingMode);
                }
                if (this.#voiceoverAsset) {
                    void this.#voiceoverAsset._bufferAtTime(0, bufferingMode);
                }
                void this.avatar._bufferAtTime(0, bufferingMode);
            }

            // prepare for playback is just preparing upcoming scenes for playback, so we don't
            // need to buffer any of the active scenes, since they will be buffered separately
            return;
        }

        return new Promise<void>((resolve, reject) => {
            this.#abortBuffering = reject;
            const sceneGroupMediaBufferPromise = this.#mediaAsset
                ? this.#mediaAsset._bufferAtTime(this.currentTime, bufferingMode)
                : Promise.resolve();
            const sceneGroupAudioBufferPromise = this.#voiceoverAsset
                ? this.#voiceoverAsset._bufferAtTime(this.currentTime, bufferingMode)
                : Promise.resolve();
            Promise.all([
                this.#assetsWorkspace._bufferAtTime(this.currentTime, bufferingMode),
                this.avatar._bufferAtTime(this.currentTime, bufferingMode),
                sceneGroupMediaBufferPromise,
                sceneGroupAudioBufferPromise,
                ...activeScenes.map(async (scene) => scene._propagateBuffering(bufferingMode)),
            ])
                .then((_) => resolve())
                .catch(reject);
        });
    }

    /**
     * Get the active scenes at the time for this scene group.
     */
    getActiveScenesAt(currentTime: number): Scene[] {
        return this.#timeline
            .getActiveChildrenIndicesAt(currentTime)
            .map((index) => this.#scenes[index]);
    }

    /**
     * Remove a scene by ID.
     */
    removeScene(sceneId: string) {
        const scene = this.#scenesById.get(sceneId);
        if (!scene) {
            return;
        }
        // remove any render tasks submitted by the scene we're about to delete
        this.root._renderSceneQueue.dequeueTasksForScene(sceneId);

        // cleanup
        const index = this.#scenes.indexOf(scene);
        this.#scenes.splice(index, 1);
        scene.destroy(true);
        this.#scenesById.set(sceneId, null);
        this.#scenesById.delete(sceneId);

        this.requiresUpdate = true;
    }

    /**
     * Apply per-scene "logical" durations (durations that do not include the transition
     * into the scene) and reconcile each scene to a final duration of
     * `logicalDuration + scene.calculateTransitionDuration()`.
     *
     * Scenes are processed in scene order so that each scene's transition cap is computed
     * against the previous scene's already-adjusted duration. Scenes whose id is not present
     * in `entries` are left untouched.
     */
    setLogicalDurations(entries: Array<{ sceneId: string; logicalDuration: number }>): void {
        const logicalById = new Map(entries.map((e) => [e.sceneId, e.logicalDuration]));
        for (const scene of this.#scenes) {
            const logical = logicalById.get(scene.id);
            if (logical === undefined) {
                continue;
            }
            scene.duration = logical;
            const transition = scene.calculateTransitionDuration();
            scene.duration = logical + transition;
        }
    }

    /**
     * Given a scene id, move the scene to a new index within the group.
     */
    moveSceneToIndex(sceneId: string, newIndex: number) {
        if (newIndex >= this.size) {
            throw Error(
                `Unable to move scene to index ${newIndex}. SceneGroup only has ${this.size} scenes.`,
            );
        }
        const oldIndex = this.#getSceneIndex(sceneId);
        const scene = this.#scenes[oldIndex];

        this.#scenes.splice(oldIndex, 1);
        this.#scenes.splice(newIndex, 0, scene);

        this.#timeline.moveChild(oldIndex, newIndex);
    }

    /**
     * List of all scenes in group
     */
    get scenes(): Scene[] {
        return this.#scenes;
    }

    /**
     * Internal Map for O(1) scene lookups by ID.
     * @internal
     */
    get _scenesById(): Map<string, Scene> {
        return this.#scenesById;
    }

    /**
     * Get size of SceneGroup (number of scenes contained in group).
     */
    get size(): number {
        return this.#scenes.length;
    }

    /**
     * The scene group's duration in milliseconds
     */
    get duration(): number {
        return this._timeline.duration;
    }

    /**
     * The start time of the scene group relative to its parent (luminary)
     */
    get startTime(): number {
        return this._timeline.startTimeRelativeToAncestor(this.root.timeline);
    }

    /**
     * The end time of the scene group relative to its parent (luminary)
     */
    get endTime(): number {
        return this.startTime + this.duration;
    }

    /**
     * Get the index of a scene in the group.
     */
    #getSceneIndex(sceneId: string) {
        return this.#scenes.findIndex((scene) => scene.id === sceneId);
    }

    /**
     * @internal
     */
    _updateForRender() {
        if (this.mediaAsset) {
            this.mediaAsset.updateForRender();
        }
        this.avatar.updateForRender();
        this.captions.updateForRender();
    }

    /**
     * @internal
     */
    get _workspace() {
        return this.#workspace;
    }

    override get slide(): null {
        // A SceneGroup is not associated with just a single slide
        return null;
    }

    override get parent() {
        return this.#sceneGroupManager;
    }

    /**
     * @internal
     */
    override _checkAndFixProperties() {
        this.#checkAndFixLinkedMediaProperties();
        this.#checkAndFixAvatarRenderUrlOnVoiceoverChange();
    }

    #checkAndFixLinkedMediaProperties() {
        const parentMediaAsset = this.#getLinkedMediaAssetSource();
        if (!parentMediaAsset) {
            return;
        }
        const linkedAssets = this.#getLinkedMediaAssets();
        if (Config.isCompositingAsset(parentMediaAsset)) {
            linkedAssets.forEach((media) => {
                media.brightness = parentMediaAsset.brightness;
            });
        } else if (Config.isMediaAsset(parentMediaAsset)) {
            linkedAssets.forEach((media) => {
                media.brightness = parentMediaAsset.brightness;
                media.contrast = parentMediaAsset.contrast;
                media.hue = parentMediaAsset.hue;
                media.saturation = parentMediaAsset.saturation;
                media.lightness = parentMediaAsset.lightness;
                media.blur = parentMediaAsset.blur;
                media.vignetting = parentMediaAsset.vignetting;
                media.vignettingAlpha = parentMediaAsset.vignettingAlpha;
                media.vignettingBlur = parentMediaAsset.vignettingBlur;
                media.noise = parentMediaAsset.noise;
                media.noiseSize = parentMediaAsset.noiseSize;
                media.enableGreyscale = parentMediaAsset.enableGreyscale;
                if (parentMediaAsset.gradingOverride) {
                    if (!media.gradingOverride) {
                        media.addGradingOverride();
                    }
                    media.gradingOverride.source = parentMediaAsset.gradingOverride.source;
                    media.gradingOverride.intensity = parentMediaAsset.gradingOverride.intensity;
                } else if (media.gradingOverride) {
                    media.removeGradingOverride();
                }
            });
        }
    }

    #checkAndFixAvatarRenderUrlOnVoiceoverChange() {
        if (!this.voiceoverAsset?.sourceData) {
            return;
        }

        const currentMp3Url = this.voiceoverAsset.sourceData.mp3_url;
        const mp3UrlChanged = this.#voiceoverAssetMp3Url !== currentMp3Url;

        if (!mp3UrlChanged) {
            return;
        }

        if (mp3UrlChanged && this.#voiceoverAssetMp3Url && this.avatar.foreground?.sourceData) {
            this.avatar.foreground.sourceData = {
                ...this.avatar.foreground.sourceData,
                render_url: null,
            };
        }

        this.#voiceoverAssetMp3Url = currentMp3Url;
    }

    override get readonlyProperties() {
        return ['id'];
    }

    /**
     * @internal
     */
    _mediaAssetCustomLoad(value: Record<string, unknown> | null) {
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        if (!value) {
            return;
        }
        this.addMediaAsset(value.id as string);
        this.mediaAsset.load(value);

        this.autoUpdate = initAutoUpdate;
    }

    /**
     * @internal
     */
    _mediaAssetCustomSave() {
        return this.mediaAsset?.save() ?? null;
    }

    /**
     * @internal
     */
    _voiceoverAssetCustomLoad(value: Record<string, unknown> | null) {
        if (!value) {
            return;
        }
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        this.addVoiceoverAsset(value.id as string);
        this.#voiceoverAsset.load(value);

        this.autoUpdate = initAutoUpdate;
    }

    override clear() {
        super.clear();

        if (this.#mediaAsset) {
            this.removeMediaAsset();
        }
        if (this.#voiceoverAsset) {
            this.removeVoiceoverAsset();
        }
        this.originalTranscript.clear();
        this.captions.clear();
    }

    override get propertiesToSerialize() {
        return ['captions', 'originalTranscript', 'mediaAsset', 'voiceoverAsset', 'avatar'];
    }

    override get editableProperties() {
        return super.editableProperties.concat(['musicVolumeOverride']);
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.#abortBuffering(
            new BufferingAbortedError(this.classType, this.index, 'destroy called'),
        );
        this.#removeHelpers();

        this.scenes.forEach((scene) => scene.destroy(options));
        if (this.#mediaAsset) {
            this.#mediaAsset.destroy(options);
        }
        if (this.#voiceoverAsset) {
            this.#voiceoverAsset.destroy();
        }
        this.avatar.destroy();
        this._timeline.destroy();
        this.#scenes = [];
        this.#scenesById = new Map<string, Scene>();
        this.#workspace.destroy(options);
    }
}
